import { useState, useEffect, useMemo, useRef, useCallback, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DollarSign, Loader2, AlertCircle, Plus, Edit } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import SearchableSelect, { type SearchableSelectOption } from '../components/SearchableSelect';
import CustomerFormModal from '../components/CustomerFormModal';
import AttachmentsSection from '../components/AttachmentsSection';
import { getCustomers, getCustomerById } from '../utils/customerApi';
import { getCustomerReceivableInvoices } from '../utils/invoiceApi';
import { useReceivePaymentsHubOptional } from './ReceivePaymentsHubContext';
import { canEditPaymentTransaction, canManageInvoices } from '../utils/salesTransactionPermissions';
import {
  applyCustomerPaymentAllocations,
  createCustomerPayment,
  getCustomerPayments,
} from '../utils/customerPaymentApi';
import { listStagedAttachments, syncStagedAttachmentNotes } from '../utils/attachmentApi';
import {
  CUSTOMER_PAYMENT_UPLOAD_SESSION_KEY,
  getOrCreateUploadSessionId,
} from '../utils/uploadSession';
import { ApiValidationError } from '../types/auth';
import type { Customer } from '../types/customer';
import type { CustomerPayment, CustomerPaymentMethod } from '../types/customerPayment';
import type { Invoice } from '../types/invoice';
import type { EditableAttachment, PendingAttachment } from '../types/attachmentsForm';
import {
  getStagedAttachmentIds,
  hasPendingUploadFailed,
  hasPendingUploadInProgress,
  stagedAttachmentToPending,
} from '../types/attachmentsForm';
import { formatCurrency } from '../utils/formatCurrency';
import { toast, toastApiError } from '../utils/toast';
import { todayLocalIso } from '../utils/localDate';

const PAYMENT_METHODS: { value: CustomerPaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'ONLINE', label: 'Online' },
];

interface OpenInvoiceLine {
  invoice_id: number;
  invoice_number: string;
  txn_date: string;
  total: number;
  balance: number;
  selected: boolean;
  paymentAmount: string;
}

const MIN_ALLOCATION = 0.01;

const customerToOption = (customer: Customer): SearchableSelectOption => ({
  value: customer.id,
  label: customer.display_name,
  description: [customer.email, customer.phone].filter(Boolean).join(' · ') || undefined,
});

const today = todayLocalIso;

const parsePayment = (value: string) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const canRecordCustomerPayment = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const receivableInvoicesToLines = (invoices: Invoice[]): OpenInvoiceLine[] =>
  invoices.map((inv) => ({
    invoice_id: inv.id,
    invoice_number: inv.invoice_number,
    txn_date: inv.txn_date.slice(0, 10),
    total: parseFloat(inv.total_amount),
    balance: parseFloat(inv.balance),
    selected: false,
    paymentAmount: '',
  }));

const autoApplyFifo = (lines: OpenInvoiceLine[], amount: number): OpenInvoiceLine[] => {
  let remaining = amount;
  return lines.map((line) => {
    if (remaining <= 0) {
      return { ...line, selected: false, paymentAmount: '' };
    }
    const applied = Math.min(remaining, line.balance);
    remaining -= applied;
    return {
      ...line,
      selected: applied > 0,
      paymentAmount: applied > 0 ? applied.toFixed(2) : '',
    };
  });
};

const formatPaymentSuccessMessage = (payment: CustomerPayment): string => {
  const total = formatCurrency(parseFloat(payment.total_amount));
  const unapplied = parseFloat(payment.unapplied_amount);
  const allocationCount = payment.allocations.length;

  if (allocationCount === 0) {
    return `Advance payment ${payment.payment_number} of ${total} recorded as unapplied credit.`;
  }

  if (unapplied > 0.01) {
    return `Payment ${payment.payment_number} of ${total} recorded (${formatCurrency(parseFloat(payment.applied_amount))} applied, ${formatCurrency(unapplied)} unapplied credit).`;
  }

  return `Payment ${payment.payment_number} of ${total} applied to ${allocationCount} invoice(s).`;
};

const formatCreditApplySuccessMessage = (payment: CustomerPayment): string => {
  const unapplied = parseFloat(payment.unapplied_amount);
  if (unapplied > MIN_ALLOCATION) {
    return `Credit from ${payment.payment_number} applied to invoices. ${formatCurrency(unapplied)} remains unapplied.`;
  }
  return `Credit from ${payment.payment_number} fully applied to invoices.`;
};

type PaymentMode = 'new' | 'credit';

export type ReceivePaymentsProps = {
  embedded?: boolean;
  initialCustomerId?: number;
  initialInvoiceId?: number;
  onClose?: () => void;
  onPaymentSaved?: (payment: CustomerPayment) => void;
};

const ReceivePayments = ({
  embedded = false,
  initialCustomerId,
  initialInvoiceId,
  onClose,
  onPaymentSaved,
}: ReceivePaymentsProps = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const receivePaymentsHub = useReceivePaymentsHubOptional();
  const routeState = location.state as { customerId?: number; invoiceId?: number } | null;
  const resolvedInitialCustomerId = embedded
    ? (initialCustomerId ?? 0)
    : (routeState?.customerId ?? 0);
  const resolvedInitialInvoiceId = embedded
    ? (initialInvoiceId ?? null)
    : (routeState?.invoiceId ?? null);
  const preselectedInvoiceIdRef = useRef<number | null>(resolvedInitialInvoiceId);
  const loadGenRef = useRef(0);
  const customerIdRef = useRef(resolvedInitialCustomerId);
  const customerLookupRef = useRef<Map<number, Customer>>(new Map());
  const selectedCustomerOptionRef = useRef<SearchableSelectOption | null>(null);
  const [customerOptions, setCustomerOptions] = useState<SearchableSelectOption[]>([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState<SearchableSelectOption | null>(
    null
  );
  const [customerId, setCustomerId] = useState(resolvedInitialCustomerId);
  const [customerName, setCustomerName] = useState('');
  const [lines, setLines] = useState<OpenInvoiceLine[]>([]);
  const [totalDue, setTotalDue] = useState(0);
  const [unappliedTotal, setUnappliedTotal] = useState(0);
  const [unappliedPayments, setUnappliedPayments] = useState<CustomerPayment[]>([]);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('new');
  const [selectedCreditPaymentId, setSelectedCreditPaymentId] = useState(0);

  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);

  const [paymentDate, setPaymentDate] = useState(today());
  const [paymentMethod, setPaymentMethod] = useState<CustomerPaymentMethod | ''>('');
  const [referenceNo, setReferenceNo] = useState('');
  const [memo, setMemo] = useState('');
  const [amountReceived, setAmountReceived] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [existingAttachments] = useState<EditableAttachment[]>([]);
  const [uploadSessionId] = useState(() =>
    getOrCreateUploadSessionId(CUSTOMER_PAYMENT_UPLOAD_SESSION_KEY)
  );

  useEffect(() => {
    customerIdRef.current = customerId;
  }, [customerId]);

  useEffect(() => {
    if (embedded && !canRecordCustomerPayment()) {
      onClose?.();
    }
  }, [embedded, onClose]);

  const loadStagedAttachments = useCallback(async () => {
    const staged = await listStagedAttachments({
      upload_session_id: uploadSessionId,
      intended_attachable_type: 'payment',
    }).catch((err) => {
      console.error('[ReceivePayments] Failed to load staged attachments', err);
      return [];
    });
    setPendingAttachments(staged.map(stagedAttachmentToPending));
  }, [uploadSessionId]);

  useEffect(() => {
    void loadStagedAttachments();
  }, [loadStagedAttachments]);

  const buildAttachmentFields = () => {
    const stagedIds = getStagedAttachmentIds(pendingAttachments);
    return stagedIds.length > 0 ? { staged_attachment_ids: stagedIds } : {};
  };

  const finishPaymentSaved = (payment: CustomerPayment, successMessage: string) => {
    if (embedded) {
      toast.success(successMessage);
      onPaymentSaved?.(payment);
      return;
    }
    navigate('/sales-transactions', {
      state: { successMessage, customerId },
    });
  };

  const loadOpenInvoices = async (id: number) => {
    const gen = ++loadGenRef.current;
    setLoadingInvoices(true);
    setError(null);
    try {
      const [receivableInvoices, unappliedPaymentsList] = await Promise.all([
        getCustomerReceivableInvoices(id),
        getCustomerPayments({ customer_id: id, unapplied: true }),
      ]);
      if (gen !== loadGenRef.current) return;
      const activeUnappliedPayments = unappliedPaymentsList.filter(
        (payment) => (payment.status ?? 'POSTED') !== 'VOIDED'
      );
      let openLines = receivableInvoicesToLines(receivableInvoices);
      const preId = preselectedInvoiceIdRef.current;
      if (preId) {
        preselectedInvoiceIdRef.current = null;
        openLines = openLines.map((line) =>
          line.invoice_id === preId
            ? { ...line, selected: true, paymentAmount: line.balance.toFixed(2) }
            : line
        );
      }
      const customer =
        customerLookupRef.current.get(id) ??
        (await getCustomerById(id).catch(() => null));
      if (customer) {
        customerLookupRef.current.set(id, customer);
      }
      const unapplied = activeUnappliedPayments.reduce(
        (sum, payment) => sum + parseFloat(payment.unapplied_amount),
        0
      );
      setCustomerName(
        customer?.display_name ?? selectedCustomerOptionRef.current?.label ?? ''
      );
      setTotalDue(openLines.reduce((sum, line) => sum + line.balance, 0));
      setUnappliedTotal(unapplied);
      setUnappliedPayments(activeUnappliedPayments);
      setPaymentMode('new');
      setSelectedCreditPaymentId(0);
      setLines(openLines);
      setAmountReceived('');
      setReferenceNo('');
      setMemo('');
      setPaymentDate(today());
      setPaymentMethod('');
    } catch (err) {
      if (gen !== loadGenRef.current) return;
      setLines([]);
      setTotalDue(0);
      setUnappliedTotal(0);
      setUnappliedPayments([]);
      setPaymentMode('new');
      setSelectedCreditPaymentId(0);
      setError(err instanceof Error ? err.message : 'Failed to load open invoices');
    } finally {
      if (gen === loadGenRef.current) setLoadingInvoices(false);
    }
  };

  useEffect(() => {
    if (!receivePaymentsHub) return;
    receivePaymentsHub.registerRefetch(() => {
      const id = customerIdRef.current;
      if (id > 0) {
        void loadOpenInvoices(id);
      }
    });
  }, [receivePaymentsHub, customerId]);

  useEffect(() => {
    selectedCustomerOptionRef.current = selectedCustomerOption;
  }, [selectedCustomerOption]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setCustomerSearchLoading(true);
      try {
        const result = await getCustomers({
          search: customerSearchInput.trim() || undefined,
          per_page: 20,
          page: 1,
        });
        for (const customer of result.customers) {
          customerLookupRef.current.set(customer.id, customer);
        }
        const options = result.customers.map(customerToOption);
        const selected = selectedCustomerOptionRef.current;
        setCustomerOptions(
          selected && !options.some((opt) => String(opt.value) === String(selected.value))
            ? [selected, ...options]
            : options
        );
      } catch {
        // silently ignore — dropdown just shows empty
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchInput]);

  useEffect(() => {
    if (!resolvedInitialCustomerId) return;

    const id = resolvedInitialCustomerId;
    void (async () => {
      try {
        const customer = await getCustomerById(id);
        customerLookupRef.current.set(id, customer);
        const option = customerToOption(customer);
        setSelectedCustomerOption(option);
        selectedCustomerOptionRef.current = option;
        setCustomerOptions([option]);
        setCustomerName(customer.display_name);
      } catch {
        // loadOpenInvoices will still run; name may stay empty until resolved
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!embedded && routeState?.customerId) {
      window.history.replaceState({}, document.title);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!customerId) {
      setLines([]);
      setTotalDue(0);
      setUnappliedTotal(0);
      setUnappliedPayments([]);
      setPaymentMode('new');
      setSelectedCreditPaymentId(0);
      setCustomerName('');
      setAmountReceived('');
      return;
    }
    loadOpenInvoices(customerId);
  }, [customerId]);

  const totalApplied = useMemo(
    () => lines.reduce((sum, line) => sum + parsePayment(line.paymentAmount), 0),
    [lines]
  );

  const enteredAmount = useMemo(() => parsePayment(amountReceived), [amountReceived]);

  const selectedCreditPayment = useMemo(
    () => unappliedPayments.find((payment) => payment.id === selectedCreditPaymentId) ?? null,
    [unappliedPayments, selectedCreditPaymentId]
  );

  const creditAvailable = useMemo(
    () => (selectedCreditPayment ? parseFloat(selectedCreditPayment.unapplied_amount) : 0),
    [selectedCreditPayment]
  );

  const unappliedPreview = useMemo(
    () => Math.max(0, enteredAmount - totalApplied),
    [enteredAmount, totalApplied]
  );

  const canRecordAppliedPayment = totalApplied >= MIN_ALLOCATION;
  const canRecordUnappliedPayment =
    paymentMode === 'new' && !canRecordAppliedPayment && enteredAmount >= MIN_ALLOCATION;
  const canApplyCredit =
    paymentMode === 'credit' && selectedCreditPaymentId > 0 && totalApplied >= MIN_ALLOCATION;

  const resetLineAllocations = () => {
    setLines((prev) => prev.map((line) => ({ ...line, selected: false, paymentAmount: '' })));
    setAmountReceived('');
    setError(null);
  };

  const switchPaymentMode = (mode: PaymentMode) => {
    setPaymentMode(mode);
    setSelectedCreditPaymentId(0);
    resetLineAllocations();
  };

  const buildAllocations = () =>
    lines
      .map((line) => ({
        invoice_id: line.invoice_id,
        amount_applied: parsePayment(line.paymentAmount),
      }))
      .filter((row) => row.amount_applied >= MIN_ALLOCATION);

  const selectedCount = useMemo(
    () => lines.filter((line) => line.selected && parsePayment(line.paymentAmount) > 0).length,
    [lines]
  );

  const allSelected =
    lines.length > 0 &&
    lines.every((line) => line.selected && parsePayment(line.paymentAmount) === line.balance);

  const handleCustomerChange = async (value: number | string) => {
    const id = Number(value);
    setCustomerId(id);

    if (!id) {
      setSelectedCustomerOption(null);
      selectedCustomerOptionRef.current = null;
      setCustomerName('');
      return;
    }

    let customer = customerLookupRef.current.get(id);
    if (!customer) {
      try {
        customer = await getCustomerById(id);
        customerLookupRef.current.set(id, customer);
      } catch {
        return;
      }
    }

    const option = customerToOption(customer);
    setSelectedCustomerOption(option);
    selectedCustomerOptionRef.current = option;
    setCustomerOptions((prev) =>
      prev.some((opt) => String(opt.value) === String(id)) ? prev : [option, ...prev]
    );
    setCustomerName(customer.display_name);
  };

  const handleCustomerSaved = (customer: Customer) => {
    customerLookupRef.current.set(customer.id, customer);
    const option = customerToOption(customer);
    setSelectedCustomerOption(option);
    selectedCustomerOptionRef.current = option;
    setCustomerOptions((prev) =>
      prev.some((opt) => String(opt.value) === String(customer.id)) ? prev : [option, ...prev]
    );
    setCustomerId(customer.id);
    setCustomerName(customer.display_name);
    setShowCreateCustomerModal(false);
    toast.success(`Customer "${customer.display_name}" created and selected.`);
  };

  const updateLine = (invoiceId: number, patch: Partial<OpenInvoiceLine>) => {
    setLines((prev) =>
      prev.map((line) => (line.invoice_id === invoiceId ? { ...line, ...patch } : line))
    );
  };

  const maxAmountForLine = (line: OpenInvoiceLine) => {
    const currentLineAmount = parsePayment(line.paymentAmount);
    const otherApplied = totalApplied - currentLineAmount;
    if (paymentMode === 'credit') {
      return Math.min(line.balance, Math.max(0, creditAvailable - otherApplied));
    }
    return line.balance;
  };

  const toggleLine = (invoiceId: number, checked: boolean) => {
    const line = lines.find((row) => row.invoice_id === invoiceId);
    if (!line) return;

    const maxForLine = maxAmountForLine(line);
    updateLine(invoiceId, {
      selected: checked && maxForLine >= MIN_ALLOCATION,
      paymentAmount: checked && maxForLine >= MIN_ALLOCATION ? maxForLine.toFixed(2) : '',
    });
  };

  const handlePaymentChange = (invoiceId: number, value: string) => {
    const line = lines.find((row) => row.invoice_id === invoiceId);
    if (!line) return;

    const currentLineAmount = parsePayment(line.paymentAmount);
    const otherApplied = totalApplied - currentLineAmount;
    const maxForLine =
      paymentMode === 'credit'
        ? Math.min(line.balance, Math.max(0, creditAvailable - otherApplied))
        : line.balance;

    const amount = parsePayment(value);
    const capped = amount > maxForLine ? maxForLine.toFixed(2) : value;

    updateLine(invoiceId, {
      paymentAmount: capped,
      selected: parsePayment(capped) >= MIN_ALLOCATION,
    });
  };

  const openAmendInvoice = (invoiceId: number) => {
    if (!canManageInvoices()) return;
    const returnTo = `${location.pathname}${location.search}`;
    navigate(`invoice/${invoiceId}`, {
      preventScrollReset: true,
      state: { returnTo },
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && paymentMode === 'credit') {
      if (!selectedCreditPaymentId || creditAvailable < MIN_ALLOCATION) {
        setError('Select an unapplied payment with available credit.');
        return;
      }
      setError(null);
      setLines((prev) => autoApplyFifo(prev, creditAvailable));
      return;
    }

    setLines((prev) =>
      prev.map((line) => ({
        ...line,
        selected: checked,
        paymentAmount: checked ? line.balance.toFixed(2) : '',
      }))
    );
  };

  const handleClearPayments = () => {
    resetLineAllocations();
  };

  const handleAutoApply = () => {
    if (paymentMode === 'credit') {
      if (!selectedCreditPaymentId || creditAvailable < MIN_ALLOCATION) {
        setError('Select an unapplied payment with available credit.');
        return;
      }
      setError(null);
      setLines((prev) => autoApplyFifo(prev, creditAvailable));
      return;
    }

    const amount = parsePayment(amountReceived);
    if (amount < MIN_ALLOCATION) {
      setError('Enter an amount received to auto-apply.');
      return;
    }
    if (amount > totalDue + MIN_ALLOCATION) {
      setError(`Amount cannot exceed total open balance of ${formatCurrency(totalDue)}.`);
      return;
    }
    setError(null);
    setLines((prev) => autoApplyFifo(prev, amount));
  };

  const handlePayAll = () => {
    if (paymentMode === 'credit') {
      if (!selectedCreditPaymentId || creditAvailable < MIN_ALLOCATION) {
        setError('Select an unapplied payment with available credit.');
        return;
      }
      setError(null);
      setLines((prev) => autoApplyFifo(prev, Math.min(totalDue, creditAvailable)));
      return;
    }

    handleSelectAll(true);
    setAmountReceived(totalDue.toFixed(2));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await submitPayment(false);
  };

  const handleSubmitUnapplied = async () => {
    await submitPayment(true);
  };

  const handleSubmitCredit = async () => {
    await submitCreditAllocation();
  };

  const validateLineAllocations = (allocations: { invoice_id: number; amount_applied: number }[]) => {
    if (allocations.length === 0) {
      setError('Select at least one invoice and enter a payment amount.');
      return false;
    }

    for (const line of lines) {
      const amount = parsePayment(line.paymentAmount);
      if (amount > line.balance + MIN_ALLOCATION) {
        setError(`Payment for ${line.invoice_number} cannot exceed ${formatCurrency(line.balance)}.`);
        return false;
      }
    }

    return true;
  };

  const submitCreditAllocation = async () => {
    if (!customerId || !selectedCreditPaymentId || !canRecordCustomerPayment()) return;

    setError(null);
    setFieldErrors({});

    const allocations = buildAllocations();
    if (!validateLineAllocations(allocations)) return;

    const appliedTotal = allocations.reduce((sum, row) => sum + row.amount_applied, 0);
    if (appliedTotal > creditAvailable + MIN_ALLOCATION) {
      setError(
        `Total applied cannot exceed available credit of ${formatCurrency(creditAvailable)} on ${selectedCreditPayment?.payment_number ?? 'this payment'}.`
      );
      return;
    }

    setSubmitting(true);
    try {
      const payment = await applyCustomerPaymentAllocations(selectedCreditPaymentId, { allocations });
      finishPaymentSaved(payment, formatCreditApplySuccessMessage(payment));
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setError(err.message);
      } else {
        toastApiError(err, 'Failed to apply unapplied credit');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitPayment = async (unappliedOnly: boolean) => {
    if (!customerId || !canRecordCustomerPayment()) return;

    setError(null);
    setFieldErrors({});

    const allocations = unappliedOnly ? [] : buildAllocations();

    if (!unappliedOnly && !validateLineAllocations(allocations)) return;

    const appliedTotal = allocations.reduce((sum, row) => sum + row.amount_applied, 0);
    const enteredTotal = parsePayment(amountReceived);
    const totalAmount = unappliedOnly
      ? enteredTotal
      : enteredTotal > appliedTotal
        ? enteredTotal
        : appliedTotal;

    if (totalAmount < MIN_ALLOCATION) {
      setError(
        unappliedOnly
          ? 'Enter an amount received of at least 0.01 to record an advance payment.'
          : 'Total payment amount must be at least 0.01.'
      );
      return;
    }

    if (!unappliedOnly && appliedTotal > totalAmount + MIN_ALLOCATION) {
      setError('Total applied cannot exceed the payment amount received.');
      return;
    }

    if (!paymentMethod) {
      setError('Select a payment method.');
      return;
    }

    if (paymentMethod === 'CHEQUE' && !referenceNo.trim()) {
      setError('Reference number is required for cheque payments.');
      return;
    }

    if (hasPendingUploadInProgress(pendingAttachments)) {
      setError('Please wait for file uploads to finish before saving.');
      return;
    }
    if (hasPendingUploadFailed(pendingAttachments)) {
      setError('Remove or retry failed file uploads before saving.');
      return;
    }

    const hasStagedNoteWork = pendingAttachments.some(
      (row) => row.uploadStatus === 'staged' && row.note !== (row.attachment?.note ?? '')
    );

    setSubmitting(true);
    try {
      if (hasStagedNoteWork) {
        const noteFailures = await syncStagedAttachmentNotes(pendingAttachments);
        if (noteFailures.length > 0) {
          setError(`Could not update attachment notes: ${noteFailures.join(' ')}`);
          return;
        }
      }

      const payment = await createCustomerPayment({
        customer_id: customerId,
        txn_date: paymentDate,
        total_amount: totalAmount,
        payment_method: paymentMethod,
        ref_number: referenceNo.trim() || null,
        memo: memo.trim() || null,
        allocations,
        ...buildAttachmentFields(),
      });

      finishPaymentSaved(payment, formatPaymentSuccessMessage(payment));
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setError(err.message);
      } else {
        toastApiError(err, 'Failed to record payment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!canRecordCustomerPayment()) {
    if (embedded) {
      return (
        <div className="flex flex-col h-full min-h-0 bg-gray-100 p-6 items-center justify-center">
          <p className="text-gray-600 text-sm">
            Only administrators and accountants can record customer payments.
          </p>
        </div>
      );
    }

    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <main className="flex-1 p-6 flex items-center justify-center min-h-0">
          <p className="text-gray-600 text-sm">
            Only administrators and accountants can record customer payments.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div
      className={
        embedded ? 'flex flex-col h-full min-h-0 bg-gray-100' : 'flex h-screen bg-gray-100'
      }
    >
      {!embedded && <Sidebar />}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <div className={`flex-1 overflow-auto ${embedded ? 'p-4' : 'p-4 md:p-6'}`}>
        <div className="w-full max-w-full">
          <div className={embedded ? 'mb-4' : 'mb-6'}>
            <h1 className={`font-semibold text-gray-900 ${embedded ? 'text-lg' : 'text-xl md:text-2xl'}`}>
              Receive Payment
            </h1>
            {!embedded && (
              <p className="mt-1 text-sm text-gray-500">
                Record new payments, save advance credit, or apply existing unapplied credit to invoices
              </p>
            )}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm flex gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form id="receive-payment-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-white rounded-lg shadow p-5">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Customer <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <SearchableSelect
                      className="flex-1 min-w-0"
                      options={customerOptions}
                      value={customerId}
                      onChange={(value) => void handleCustomerChange(value)}
                      placeholder="Select a customer"
                      searchPlaceholder="Search customers..."
                      emptyMessage="No customers found"
                      onSearchChange={setCustomerSearchInput}
                      isLoading={customerSearchLoading}
                    />
                      <button
                        type="button"
                        onClick={() => setShowCreateCustomerModal(true)}
                        className="flex-shrink-0 inline-flex items-center px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
                        title="Create new customer"
                      >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {customerId > 0 && (
                  <div className="flex flex-col justify-end gap-2 w-full">
                    <div className="grid grid-cols-2 gap-3 w-full">
                      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Open balance</p>
                        <p className="text-lg font-semibold text-red-600">
                          {loadingInvoices ? '—' : formatCurrency(totalDue)}
                        </p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Unapplied payment</p>
                        <p className="text-lg font-semibold text-amber-700">
                          {loadingInvoices ? '—' : formatCurrency(unappliedTotal)}
                        </p>
                      </div>
                    </div>
                    {customerName && <p className="text-sm text-gray-600">{customerName}</p>}
                  </div>
                )}
              </div>
            </div>

            {customerId > 0 && (
              <>
                {unappliedPayments.length > 0 && (
                  <div className="bg-white rounded-lg shadow p-5">
                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                      Payment action
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => switchPaymentMode('new')}
                        className={`px-4 py-2 text-sm font-medium rounded-lg border ${
                          paymentMode === 'new'
                            ? 'bg-slate-800 text-white border-slate-800'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        Receive new payment
                      </button>
                      <button
                        type="button"
                        onClick={() => switchPaymentMode('credit')}
                        className={`px-4 py-2 text-sm font-medium rounded-lg border ${
                          paymentMode === 'credit'
                            ? 'bg-amber-700 text-white border-amber-700'
                            : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-50'
                        }`}
                      >
                        Apply unapplied credit
                      </button>
                    </div>
                    {paymentMode === 'credit' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Unapplied payment <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-wrap items-center gap-2 max-w-xl">
                          <select
                            value={selectedCreditPaymentId}
                            onChange={(e) => {
                              setSelectedCreditPaymentId(Number(e.target.value));
                              resetLineAllocations();
                            }}
                            className="flex-1 min-w-[12rem] border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                          >
                            <option value={0}>Select a payment</option>
                            {unappliedPayments.map((payment) => (
                              <option key={payment.id} value={payment.id}>
                                {payment.payment_number} —{' '}
                                {formatCurrency(parseFloat(payment.unapplied_amount))} available (
                                {payment.txn_date.slice(0, 10)})
                              </option>
                            ))}
                          </select>
                          {selectedCreditPayment &&
                            canEditPaymentTransaction(selectedCreditPayment) && (
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/receive-payments/payment/${selectedCreditPayment.id}/allocations`
                                  )
                                }
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                              >
                                <Edit className="h-4 w-4" />
                                Edit
                              </button>
                            )}
                        </div>
                        {selectedCreditPayment && (
                          <p className="mt-2 text-xs text-gray-500">
                            {formatCurrency(creditAvailable)} available from{' '}
                            {selectedCreditPayment.payment_number}. No new cash or journal entry —
                            credit is applied to invoices only.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {paymentMode === 'new' && (
                <>
                <div className="bg-white rounded-lg shadow p-5">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                        Payment details
                      </h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment date</label>
                          <input
                            type="date"
                            value={paymentDate}
                            onChange={(e) => setPaymentDate(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          />
                          {fieldErrors.txn_date?.[0] && (
                            <p className="mt-1 text-xs text-red-600">{fieldErrors.txn_date[0]}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Payment method <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={paymentMethod}
                            onChange={(e) =>
                              setPaymentMethod(e.target.value as CustomerPaymentMethod | '')
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                          >
                            <option value="">Select payment method</option>
                            {PAYMENT_METHODS.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </select>
                          {fieldErrors.payment_method?.[0] && (
                            <p className="mt-1 text-xs text-red-600">{fieldErrors.payment_method[0]}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Reference no.
                            {paymentMethod === 'CHEQUE' && <span className="text-red-500"> *</span>}
                          </label>
                          <input
                            type="text"
                            maxLength={100}
                            value={referenceNo}
                            onChange={(e) => setReferenceNo(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            placeholder={paymentMethod === 'CHEQUE' ? 'Cheque number' : 'Bank / cheque ref'}
                            required={paymentMethod === 'CHEQUE'}
                          />
                          {fieldErrors.ref_number?.[0] && (
                            <p className="mt-1 text-xs text-red-600">{fieldErrors.ref_number[0]}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Memo</label>
                          <input
                            type="text"
                            value={memo}
                            onChange={(e) => setMemo(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-3">
                        Payment number (PAY-0001, …) is assigned automatically on save.
                      </p>
                    </div>

                    <AttachmentsSection
                      title="Attachments"
                      description="Bank receipts, cheque scans, and other supporting documents. Files upload immediately and link when you save."
                      showIncludeOnSend={false}
                      parentIsDraft={false}
                      stageOnPick
                      intendedAttachableType="payment"
                      uploadSessionId={uploadSessionId}
                      pending={pendingAttachments}
                      onPendingChange={setPendingAttachments}
                      existing={existingAttachments}
                      onExistingChange={() => {}}
                      disabled={submitting}
                    />
                  </div>
                </div>
                </>
                )}

                <div className="bg-white rounded-lg shadow">
                  <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                        Outstanding invoices
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {paymentMode === 'credit'
                          ? 'Allocate existing unapplied credit to posted invoices'
                          : 'Posted invoices with open or partial balance'}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {paymentMode === 'new' ? (
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600 whitespace-nowrap">Amount received</label>
                          <input
                            type="number"
                            step="any"
                            min="0"
                            value={amountReceived}
                            onChange={(e) => setAmountReceived(e.target.value)}
                            className="w-32 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-600 whitespace-nowrap">Credit available</span>
                          <span className="font-semibold text-amber-700">
                            {selectedCreditPayment
                              ? formatCurrency(creditAvailable)
                              : '—'}
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={handleAutoApply}
                        className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
                      >
                        Auto apply
                      </button>
                      <button
                        type="button"
                        onClick={handlePayAll}
                        className="px-3 py-1.5 text-sm font-medium text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-lg"
                      >
                        Pay all
                      </button>
                      <button
                        type="button"
                        onClick={handleClearPayments}
                        className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {loadingInvoices ? (
                    <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Loading open invoices...
                    </div>
                  ) : lines.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-12 px-4">
                      {paymentMode === 'credit'
                        ? 'No open invoices for this customer. Post an invoice before applying credit.'
                        : 'No open invoices for this customer. Enter an amount received above and use Record as unapplied payment to save an advance.'}
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 w-10">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-slate-800"
                                aria-label="Select all invoices"
                              />
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-40">
                              Payment
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {lines.map((line) => {
                            const payment = parsePayment(line.paymentAmount);
                            const overBalance = payment > line.balance + MIN_ALLOCATION;
                            const overCredit =
                              paymentMode === 'credit' &&
                              totalApplied > creditAvailable + MIN_ALLOCATION;

                            return (
                              <tr key={line.invoice_id} className={line.selected ? 'bg-slate-50/60' : undefined}>
                                <td className="px-4 py-2">
                                  <input
                                    type="checkbox"
                                    checked={line.selected}
                                    onChange={(e) => toggleLine(line.invoice_id, e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-slate-800"
                                    aria-label={`Select invoice ${line.invoice_number}`}
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">{line.txn_date}</td>
                                <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                  {canManageInvoices() ? (
                                    <button
                                      type="button"
                                      onClick={() => openAmendInvoice(line.invoice_id)}
                                      className="text-slate-800 hover:text-slate-600 hover:underline font-mono"
                                      title="Edit"
                                    >
                                      {line.invoice_number}
                                    </button>
                                  ) : (
                                    line.invoice_number
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 text-right whitespace-nowrap">
                                  {formatCurrency(line.total)}
                                </td>
                                <td className="px-4 py-2 text-sm font-medium text-red-600 text-right whitespace-nowrap">
                                  {formatCurrency(line.balance)}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <input
                                      type="number"
                                      step="any"
                                      min="0"
                                      max={line.balance}
                                      value={line.paymentAmount}
                                      onChange={(e) => handlePaymentChange(line.invoice_id, e.target.value)}
                                      onFocus={(e) => e.target.select()}
                                      className={`w-28 border rounded-lg px-2.5 py-1.5 text-sm text-right ${
                                        overBalance || overCredit
                                          ? 'border-red-400 bg-red-50'
                                          : 'border-gray-300'
                                      }`}
                                      placeholder="0.00"
                                    />
                                    {line.balance > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => toggleLine(line.invoice_id, true)}
                                        className="text-xs text-slate-600 hover:text-slate-900 whitespace-nowrap px-1"
                                        title="Pay full balance"
                                      >
                                        Full
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </form>
        </div>
      </div>

      {customerId > 0 && (
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 md:px-6 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              {lines.length > 0 && (
                <>
                  <span className="text-gray-600">
                    Invoices selected:{' '}
                    <span className="font-medium text-gray-900">{selectedCount}</span>
                  </span>
                  <span className="text-gray-600">
                    Open balance:{' '}
                    <span className="font-medium text-red-600">{formatCurrency(totalDue)}</span>
                  </span>
                </>
              )}
              {paymentMode === 'credit' && selectedCreditPayment && (
                <span className="text-gray-600">
                  Credit remaining:{' '}
                  <span className="font-medium text-amber-700">
                    {formatCurrency(Math.max(0, creditAvailable - totalApplied))}
                  </span>
                </span>
              )}
              {paymentMode === 'new' && unappliedPreview > MIN_ALLOCATION && (
                <span className="text-gray-600">
                  Unapplied credit:{' '}
                  <span className="font-medium text-amber-700">{formatCurrency(unappliedPreview)}</span>
                </span>
              )}
              {lines.length > 0 && (
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-slate-600" />
                  <span className="text-gray-600">Amount to apply</span>
                  <span className="text-lg font-semibold text-slate-900">
                    {formatCurrency(totalApplied)}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {embedded && onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
              {paymentMode === 'credit' ? (
                <button
                  type="button"
                  onClick={handleSubmitCredit}
                  disabled={submitting || !canApplyCredit}
                  className="flex items-center px-6 py-2.5 bg-amber-700 text-white rounded-lg hover:bg-amber-800 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Applying...
                    </>
                  ) : (
                    'Apply credit to invoices'
                  )}
                </button>
              ) : canRecordAppliedPayment ? (
                <button
                  type="submit"
                  form="receive-payment-form"
                  disabled={submitting}
                  className="flex items-center px-6 py-2.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Record payment'
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmitUnapplied}
                  disabled={submitting || !canRecordUnappliedPayment}
                  className="flex items-center px-6 py-2.5 bg-amber-700 text-white rounded-lg hover:bg-amber-800 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Record as unapplied payment'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      </div>

      {showCreateCustomerModal && (
        <CustomerFormModal
          subtitle="Adds the customer to your list and selects them for this payment."
          onClose={() => setShowCreateCustomerModal(false)}
          onSaved={handleCustomerSaved}
        />
      )}
    </div>
  );
};

export default ReceivePayments;
